// The two review-gate verdicts the babysit decision reads.
//
//   - `reviewGateRefusalHolds` — what a head the repo's OWN review config DECLINED
//     means for the review-absent rung (`./review-absent.ts`) AND for the worker's
//     review asks (`apps/worker` `babysit/idle-review.ts`): a clockless HOLD that
//     names the exit that releases it, or the bounded wait today's behaviour keeps.
//   - `reMintFailedReview` — whether a review of OURS that ended failed (or unserved)
//     may be re-minted for the head the caller holds.
//
// Pure: no I/O, no clock, no import of `@developerz/review` — domain imports nothing
// internal, so the planner's result arrives as the structural `{ kind, reason }` the
// worker read off it (the shape `planReview` / `planReviewTakeover` return).

/**
 * The review planner's result for ONE head (`packages/review` `planReview` /
 * `planReviewTakeover`). Structural, so this module never depends on the review
 * package: the worker hands the plan over verbatim.
 *
 * - `enqueue` — our reviewer will read this head.
 * - `defer` — the head belongs to an INCUMBENT reviewer (CodeRabbit), not to us.
 * - `skip` — the repo's OWN config declined the head: no verdict of ours will come.
 */
export type ReviewGatePlan = {
  readonly kind: 'enqueue' | 'defer' | 'skip';
  /** The gate's own reason — greppable, and `prefix:value` for the refusals that carry one. */
  readonly reason: string;
};

/**
 * What a config-driven refusal means for the review-absent rung.
 *
 * - `hold` — the head WAITS, with NO clock: no verdict of ours can land while the
 *   repo's own config declines it, so no wait bound can end this hold. The reason
 *   NAMES the exit that releases it — the change that lets our reviewer read the head.
 * - `wait` — the bounded wait and its `review_absent` escalation stand, which is what
 *   an AI-MANAGED PR keeps: the platform finishes its own pull requests, so a head its
 *   repo's config declined is a wait with an owner, not a park with nobody told.
 */
export type ReviewGateRefusalVerdict =
  | { readonly kind: 'hold'; readonly reason: string }
  | { readonly kind: 'wait' };

/**
 * EVERY skip reason the review planner can return for a head (`packages/review`
 * `planReviewablePr`), in the planner's own order.
 *
 * The reason it returns is the PREFIX of the grade — `ignored_title_keyword:WIP` for
 * the keyword `WIP` — and this list is those prefixes. `planReview` also emits
 * `action_not_reviewable:<action>`, which the TAKEOVER path (`planReviewTakeover`)
 * cannot produce; it is not here, and neither is any reason a later planner adds:
 * {@link reviewGateRefusalHolds} grades a reason it does not know as NO refusal
 * (today's behaviour), so a new reason cannot silently become a hold.
 */
export const REVIEW_GATE_SKIP_REASONS = [
  'review_disabled',
  'auto_review_off',
  'draft_pr',
  'base_branch_excluded',
  'ignored_title_keyword',
  'excluded_label',
  'no_required_label',
  'ignored_author',
] as const;

/** One skip reason's kind — {@link REVIEW_GATE_SKIP_REASONS}[number]. */
export type ReviewGateSkipReason = (typeof REVIEW_GATE_SKIP_REASONS)[number];

/** The reasons this verdict knows, as a set — the lookup a reason is graded through. */
const SKIP_REASONS: ReadonlySet<string> = new Set(REVIEW_GATE_SKIP_REASONS);

/**
 * Grade a review-gate refusal into the rung's answer: a clockless HOLD that names its
 * exit, or the bounded wait today's behaviour keeps.
 *
 * `null` when there is no refusal to grade — the plan enqueued or deferred, or its
 * reason is not one the planner produces for a head. An unknown reason is NO refusal,
 * so a reason added to the planner later cannot silently become a hold: the head keeps
 * today's behaviour (bounded wait, then `review_absent`).
 *
 * AN AI-MANAGED PR keeps the bounded wait and its `review_absent` escalation for
 * EVERY refusal — one rule, not one per refusal: the platform finishes its own pull
 * requests, so a head its repo's config declined is a wait with an owner.
 */
export function reviewGateRefusalHolds(
  plan: ReviewGatePlan,
  input: { readonly aiManagedPr: boolean },
): ReviewGateRefusalVerdict | null {
  if (plan.kind !== 'skip') return null;
  const refusal = refusalOf(plan.reason);
  if (refusal === null) return null;
  if (input.aiManagedPr) return { kind: 'wait' };
  return { kind: 'hold', reason: holdReason(plan.reason, refusal) };
}

/**
 * May the next hop re-mint a review of OURS that ended failed (or unserved) without
 * serving the head?
 *
 * ONLY for the CURRENT head: a failed review of an OLDER head says nothing about this
 * one, and re-minting it would spend this head's one review on a commit nobody is
 * looking at any more. A head that moved is a new review.
 *
 * The one-per-(PR, head) bound is NOT here — it is the `review_unserved` ask's own task
 * id (`reviewUnservedReReviewTaskId`, `apps/worker`), and this verdict neither mints
 * nor computes an id.
 */
export function reMintFailedReview(input: {
  /** The head the caller HOLDS — the live head of the pull request. */
  readonly currentHeadSha: string;
  /** The head the failed (or unserved) review was OF. */
  readonly failedReviewHeadSha: string;
}): boolean {
  return sameHead(input.failedReviewHeadSha, input.currentHeadSha);
}

/** One refusal: its kind, and the value the planner's reason carried (empty when it carried none). */
type Refusal = { readonly kind: ReviewGateSkipReason; readonly value: string };

/** The refusal a planner reason names, or `null` when it names none this verdict grades. */
function refusalOf(reason: string): Refusal | null {
  const colon = reason.indexOf(':');
  const prefix = colon === -1 ? reason : reason.slice(0, colon);
  if (!isSkipReason(prefix)) return null;
  return { kind: prefix, value: colon === -1 ? '' : reason.slice(colon + 1) };
}

/** Is this the PREFIX of one of the planner's own skip reasons? */
function isSkipReason(prefix: string): prefix is ReviewGateSkipReason {
  return SKIP_REASONS.has(prefix);
}

/** The hold's sentence: the refusal, and the EXIT that releases it. */
function holdReason(reason: string, refusal: Refusal): string {
  const exit = exitOf(refusal);
  return (
    `no review verdict on this head, and none of ours will come: this repo's own review policy declined it (${reason})` +
    ` — ${exit}`
  );
}

/**
 * The EXIT each refusal names — the change that lets our reviewer read the head again.
 * A switch over the closed set of skip reasons, so a reason added to the planner
 * cannot land here without a decision about what releases it.
 */
function exitOf(refusal: Refusal): string {
  switch (refusal.kind) {
    case 'review_disabled':
      return 'turn our reviewer on (review.enabled), or set pr.require_review: false';
    case 'auto_review_off':
      return 'turn auto-review on (review.auto_review.enabled), or set pr.require_review: false';
    case 'draft_pr':
      return 'mark the pull request ready for review — a draft is not reviewed by default';
    case 'base_branch_excluded':
      return `retarget the pull request at a base branch this repo reviews, or widen review.auto_review.base_branches to cover "${refusal.value}"`;
    case 'ignored_title_keyword':
      return `retitle the pull request so its title no longer carries "${refusal.value}", or drop that keyword from review.auto_review.ignore_title_keywords`;
    case 'excluded_label':
      return `remove the "${refusal.value}" label from the pull request, or drop it from the repo's auto-review label filters`;
    case 'no_required_label':
      return 'add one of the repo-configured required review labels to the pull request';
    case 'ignored_author':
      return `drop "${refusal.value}" from the repo's reviewer ignore list (reviews.auto_review.ignore_usernames)`;
  }
}

/**
 * Is this the SAME head? A review spec's `head_sha` may be ABBREVIATED (7+ hex), so
 * the live head and the recorded one agree when either spelling prefixes the other.
 * An unreadable sha (blank, or shorter than 7) is never the same head: unknown is not
 * "the same", it is unknown.
 *
 * THE ONE head-identity rule: `reMintFailedReview` above and the worker's own head
 * comparison (`apps/worker` `babysit/idle-review.ts`) both read THIS, so the two can
 * never disagree about which head a review was of.
 */
export function sameHead(reviewed: string, live: string): boolean {
  if (reviewed.length < 7 || live.length < 7) return false;
  return live.startsWith(reviewed) || reviewed.startsWith(live);
}
